import './CourseBigCard.scss';

import React, { useMemo } from 'react';

import { IoLockClosed } from 'react-icons/io5';

import CustomImage from '../../../../components/CustomImage/CustomImage';
import { useTheme } from '../../../../context/ThemeProvider';
import { hasFeatureAccess, truncateText } from '../../../../shared/utils';
import { FEATURES } from '../../../../types/baseTypes';
import { ICourse } from '../../../../types/courseTypes';
import {
  COURSE_CARD_FALLBACK_COVER,
  getCourseCardPresentation,
} from './courseFeedCardUtils';

export interface CourseBigCardProps {
  course: ICourse;
  onPress?: () => void;
  className?: string;
}

const CourseBigCard: React.FC<CourseBigCardProps> = ({
  course,
  onPress,
  className,
}) => {
  const { colors } = useTheme();

  const { percent, showProgress, categoryLabel, statusBadge, ctaLabel } =
    useMemo(
      () => getCourseCardPresentation(course, 'Start Learning'),
      [course],
    );

  const isPaidLocked = !!course.paidCourse;
  const rootClass = [
    'course-big-card',
    isPaidLocked ? 'course-big-card--paid-locked' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const newChapterCount = course.newChapterTag?.length ?? 0;
  const hasNewChapterFooter = newChapterCount > 0;

  return (
    <div
      className={rootClass}
      onClick={onPress}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPress?.();
        }
      }}
      role="button"
      tabIndex={0}>
      <div className="course-big-card__media">
        <CustomImage
          src={course.coverImage || COURSE_CARD_FALLBACK_COVER}
          fallbackSrc={COURSE_CARD_FALLBACK_COVER}
          alt=""
        />
        {isPaidLocked ? (
          <div className="course-big-card__media-lock" aria-hidden>
            <IoLockClosed />
          </div>
        ) : null}
      </div>
      <div className="course-big-card__content">
        <div className="course-big-card__badges">
          {categoryLabel && hasFeatureAccess(FEATURES.COURSE_RECOMMENDATION) ? (
            <span className="course-big-card__badge course-big-card__badge--category">
              {categoryLabel}
            </span>
          ) : (
            <div />
          )}
          {statusBadge === 'new' ? (
            <span className="course-big-card__badge course-big-card__badge--new">
              NEW
            </span>
          ) : null}
          {statusBadge === 'completed' ? (
            <span
              className="course-big-card__badge course-big-card__badge--completed"
              style={{ backgroundColor: colors.GREEN }}>
              COMPLETED
            </span>
          ) : null}
        </div>
        <h3 className="course-big-card__title">{course.title}</h3>
        <p className="course-big-card__instructor">
          {truncateText(
            course?.instructorName || course?.creator?.name || '',
            35,
          )}
        </p>
        <p className="course-big-card__instructor">
          {course?.numberofModules || 0} sections •{' '}
          {course?.numberOfChapters || 0} lectures
          {!!course?.courseDurationFormatted &&
            ` • ${course?.courseDurationFormatted}`}
        </p>
        {showProgress ? (
          <div className="course-big-card__progress">
            <div className="course-big-card__progress-track">
              <div
                className="course-big-card__progress-fill"
                style={{
                  width: `${percent}%`,
                  backgroundColor: colors.GREEN,
                }}
              />
            </div>
            <span className="course-big-card__progress-label">{percent}%</span>
          </div>
        ) : null}
        <button
          type="button"
          className={
            'course-big-card__cta' +
            (ctaLabel === 'Buy now to unlock'
              ? ' course-big-card__cta--paid-locked'
              : '')
          }
          onClick={(e) => {
            e.stopPropagation();
            onPress?.();
          }}>
          {ctaLabel}
        </button>
      </div>
      {hasNewChapterFooter ? (
        <div className="course-big-card__new-chapter-footer">
          <span className="course-big-card__new-chapter-pill">NEW</span>
          <span className="course-big-card__new-chapter-text">
            <strong>
              {newChapterCount} New{' '}
              {newChapterCount === 1 ? 'Chapter' : 'Chapters'}
            </strong>{' '}
            <span className="course-big-card__new-chapter-muted">
              recently added.
            </span>
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(CourseBigCard);
